// Types and presentation rules for the notifications feed.
//
// A plain .ts module with no JSX so Node's own test runner can import it under
// --experimental-strip-types. What is worth testing here is the grouping of a day
// and the wording of a kind, both of which are decisions that would otherwise be
// buried inside a component.
//
// NOTHING IS DECIDED HERE. What counts as a notification, whose it is and how old
// is too old all arrive already settled by public.notification_feed_v1. This file
// only chooses words, icons and order. If a rule ever needs computing, it belongs
// in the function, per the standing rule that domain logic lives in Postgres.

export type NotificationKind =
  | 'mention'
  | 'task_moved'
  | 'driver_state'
  | 'vehicle_state'
  | 'vehicle_offboarding'
  | 'seat_change'
  | 'driver_benched'
  | 'driver_silent'
  | 'driver_documents'
  | 'application_stalled'
  // The marketing board, migration 0152. Two of these are about content and two
  // about design, and the four standing ones are the gaps rather than the events.
  | 'content_submitted'
  | 'content_decided'
  | 'design_requested'
  | 'design_delivered'
  | 'content_awaiting_decision'
  | 'design_overdue'
  | 'design_no_date'
  | 'content_unpublished'

export type NotificationRow = {
  notif_key: string
  kind: string
  /** 'event' happened at a moment. 'standing' is true right now. */
  category: string
  occurred_at: string
  title: string
  detail: string | null
  /** 'task' | 'driver' | 'vehicle' | 'page' | 'marketing' */
  target_kind: string
  task_id: string | null
  task_ref: number | string | null
  driver_id: string | null
  vehicle_id: string | null
  content_item_id: string | null
  design_request_id: string | null
  seen_at: string | null
  dismissed_at: string | null
}

export type NotificationFeed = {
  rows: NotificationRow[]
  unread: number
  /** False when the login has no person on file. Not the same as having nothing. */
  linked: boolean
  can_see_all: boolean
  scope?: 'mine' | 'all'
}

/**
 * What each kind is called on the screen, and the one line under the heading
 * saying what it actually measures.
 *
 * The second half of driver_silent is not padding. It is the difference between
 * a screen that says a man did not come to work and a screen that says nothing
 * has come in, and only one of those two is true.
 */
export const KIND_LABELS: Record<string, { label: string; means: string }> = {
  mention: {
    label: 'Named you',
    means: 'Somebody put your name in an update on a task.',
  },
  task_moved: {
    label: 'Your tasks moved',
    means: 'Somebody other than you changed the status, group, assignees, target date or parent of a task assigned to you or naming you.',
  },
  driver_state: {
    label: 'Drivers in your book',
    means: 'A driver you manage was activated, suspended, dismissed or archived.',
  },
  vehicle_state: {
    label: 'Cars in your book',
    means: 'A car you manage changed its active state.',
  },
  vehicle_offboarding: {
    label: 'Cars leaving',
    means: 'A car you manage is being offboarded and the thirty day steps are running.',
  },
  seat_change: {
    label: 'Seats in your book',
    means: 'The allocation moved a seat on one of your cars.',
  },
  driver_benched: {
    label: 'Benched, and paid',
    means: 'A driver you manage is benched. He is on a guaranteed salary while he is not seated, so this costs money every day it is true.',
  },
  driver_silent: {
    label: 'Seated and silent',
    means: 'A driver you manage holds a seat and no shift has come in.',
  },
  driver_documents: {
    label: 'Paperwork short',
    means: 'A driver you manage is missing a required document, or one has expired.',
  },
  application_stalled: {
    label: 'Applicants waiting',
    means: 'Applications you interviewed have sat at one status longer than the provisional window for it.',
  },
  content_awaiting_decision: {
    label: 'Waiting on your decision',
    means: 'Content has been submitted for approval and no decision has been recorded. Nothing goes out until one is.',
  },
  content_submitted: {
    label: 'Sent for approval',
    means: 'Somebody submitted a piece of content and you are one of the people who can approve it.',
  },
  content_decided: {
    label: 'Decided',
    means: 'Content you wrote or own was approved, or sent back for changes.',
  },
  content_unpublished: {
    label: 'Approved, not gone out',
    means: 'Content was approved and has not been published, and either its scheduled date has passed or it never had one.',
  },
  design_requested: {
    label: 'Design asked for',
    means: 'Somebody raised a design request and it is assigned to you.',
  },
  design_delivered: {
    label: 'Your design is ready',
    means: 'A design you asked for has been delivered with a file on it.',
  },
  design_overdue: {
    label: 'Design past its date',
    means: 'An open design request is past the date it was needed by. Both the designer and whoever asked see this.',
  },
  design_no_date: {
    label: 'Design with no date',
    means: 'A design request you raised is open and carries no date, so it can never be late and cannot be planned around.',
  },
}

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind]?.label ?? kind
}

export function kindMeans(kind: string): string | null {
  return KIND_LABELS[kind]?.means ?? null
}

/**
 * The order the kinds are read in.
 *
 * Mentions first because somebody is waiting on an answer. Then what moved
 * underneath you. Then the standing gaps, which are true today and will still be
 * true in an hour, so they are the ones that can wait until the top of the page
 * has been dealt with.
 */
// Events first, then the standing conditions, and inside each block the ones
// somebody is waiting on before the ones nobody is. content_awaiting_decision
// leads the standing block because it is the only one on this list where the
// whole company stops until a person acts.
const KIND_ORDER: string[] = [
  'mention',
  'content_submitted',
  'design_requested',
  'content_decided',
  'design_delivered',
  'task_moved',
  'driver_state',
  'seat_change',
  'vehicle_state',
  'vehicle_offboarding',
  'content_awaiting_decision',
  'design_overdue',
  'content_unpublished',
  'design_no_date',
  'driver_benched',
  'driver_silent',
  'driver_documents',
  'application_stalled',
]

export function kindRank(kind: string): number {
  const i = KIND_ORDER.indexOf(kind)
  return i === -1 ? KIND_ORDER.length : i
}

export type NotificationGroup = {
  kind: string
  label: string
  means: string | null
  rows: NotificationRow[]
  unread: number
}

/** Groups the feed by kind, in reading order, unread counted per group. */
export function groupByKind(rows: NotificationRow[]): NotificationGroup[] {
  const byKind = new Map<string, NotificationRow[]>()
  for (const row of rows) {
    const list = byKind.get(row.kind)
    if (list) list.push(row)
    else byKind.set(row.kind, [row])
  }
  return [...byKind.entries()]
    .map(([kind, list]) => ({
      kind,
      label: kindLabel(kind),
      means: kindMeans(kind),
      rows: list,
      unread: list.filter((r) => r.seen_at === null).length,
    }))
    .sort((a, b) => kindRank(a.kind) - kindRank(b.kind) || a.label.localeCompare(b.label))
}

/**
 * Where clicking a notification goes.
 *
 * Returns null when there is nowhere honest to send somebody. A null is rendered
 * as a row that does not pretend to be a link, which is better than a link that
 * lands on a page with no way to find the thing it was about.
 */
export function targetPath(row: NotificationRow): string | null {
  if (row.target_kind === 'task' && row.task_ref != null) return `/tasks/${row.task_ref}`
  if (row.target_kind === 'driver' && row.driver_id) return `/active?driver=${row.driver_id}`
  if (row.target_kind === 'vehicle' && row.vehicle_id) return `/fleet?vehicle=${row.vehicle_id}`
  // Marketing rows carry the thing itself, not a task reference, so the click
  // lands on the item with its panel open rather than on a board to be searched.
  // That was the whole complaint behind migration 0147.
  if (row.target_kind === 'marketing') {
    if (row.content_item_id) return `/marketing?item=${row.content_item_id}`
    if (row.design_request_id) return `/marketing?design=${row.design_request_id}`
    return '/marketing'
  }
  if (row.kind === 'application_stalled') return '/pipeline'
  return null
}

/** "3 minutes ago", "yesterday", "12 Aug". Short, because the list is long. */
export function whenLabel(iso: string, now: Date = new Date()): string {
  const then = new Date(iso)
  const ms = now.getTime() - then.getTime()
  if (Number.isNaN(ms)) return ''
  const minutes = Math.floor(ms / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  return then.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
}
